import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, signal, viewChild, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { TagComponent } from '../../../../../shared/components/tag/tag.component';
import { ResetDropdownFilterDirective } from '../../../../../shared/directives/reset-dropdown-filter.directive';
import { RequestBuilderService } from '../../../../../shared/services/request-builder.service';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectFilterOptions, SelectModule } from 'primeng/select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { CameraStatus, CameraStatusService } from '../../../services/camera-status.service';
import { CameraFilterUtils, FilterableDriver, CameraFilterOption } from '../../../services/camera-filter-utils.service';
import { Subscription } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { requireCameraValidator } from '../../../../../shared/utilities/utility-functions';

interface DriverData extends FilterableDriver {
  firstName: string;
  lastName: string;
  name: string;
  driverDefaultWithholdPercentage: number;
  isInternalOrganization: boolean;
  organizationId: number;
  equipmentIds: string;
  vehicleType?: string;
}
@Component({
  selector: 'app-assign-sub-driver-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, InputNumberModule, AutoCompleteModule, ButtonComponent, SelectModule, CheckboxModule, TagComponent, DialogModule, ResetDropdownFilterDirective, TooltipModule],
  templateUrl: './assign-sub-driver-dialog.component.html',
  styleUrl: './assign-sub-driver-dialog.component.scss',
})
export class AssignSubDriverDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly cameraStatusService = inject(CameraStatusService);

  allDrivers: DriverData[] = [];
  filteredDrivers = signal<DriverData[]>([]);
  filterValue: string = '';
  reason = '';
  selectedSubDriver: DriverData | null = null;
  driverMeetsRequirements = false;
  notifyChecked = false;
  confimActionVisible = false;
  providerTotalPay = 0;
  driverTotalPay = 0;
  payoutAdjustmentVal = 0;

  oldDriverData: any;

  readonly subDriverForm = viewChild.required<NgForm>('subDriverForm');
  loading = false;
  suggestions: string[] = [`Original driver is not available.`, `Original driver informed us ahead of time they are not available.`, `Original driver is having mechanical issues.`, `School district requested a different driver.`];

  filteredSuggestions: string[] = [];
  showAlert: boolean = false;
  private cameraSubscriptions: Subscription[] = [];
  private currentFilterCode = 'all'; // Track current active filter
  cameraStatusFilters: CameraFilterOption[] = [...CameraFilterUtils.DEFAULT_FILTER_OPTIONS];

  ngOnInit() {
    console.log(this.config.data);

    this.oldDriverData = this.config.data.oldDriverData;
    if (this.oldDriverData?.payoutAdjustment > 0) {
      this.payoutAdjustmentVal = this.oldDriverData.payoutAdjustment;
    }
    this.filteredSuggestions = [...this.suggestions];
    this.getAllDrivers();
  }

  ngAfterViewInit() {
    // Set the camera validator after the form is initialized
    setTimeout(() => {
      const subDriverControl = this.subDriverForm().form.get('subDriver');
      if (subDriverControl) {
        subDriverControl.addValidators(requireCameraValidator(this.config.data.isCameraRequired ?? false));
        subDriverControl.updateValueAndValidity();
      }
    });
  }

  updateCameraStatusFilter(selectedFiltercode: string) {
    this.cameraStatusFilters = CameraFilterUtils.updateFilterSelection(this.cameraStatusFilters, selectedFiltercode);
    this.currentFilterCode = selectedFiltercode;
    this.filterDrivers(selectedFiltercode);
  }

  filterDrivers(code: string) {
    const filtered = CameraFilterUtils.filterDrivers(this.allDrivers, code);
    this.filteredDrivers.set(filtered);
  }

  getAllDrivers() {
    this.requestBuilder.get(this.config.data.selectedDriverId ? `drivers/${this.config.data.state}?selectedPartnerId=${this.config.data.selectedDriverId}` : `drivers/${this.config.data.state}`).subscribe(res => {
      const data: DriverData[] = res.data;
      console.log(data);

      this.allDrivers = data.map(d => {
        const initial = this.cameraStatusService.getCurrentStatus(d.partnerId);
        return { ...d, name: `${d.firstName} ${d.lastName}`, cameraStatus: initial };
      });
      this.filteredDrivers.set(this.allDrivers);
      // Subscribe per driver for live camera updates
      this.cameraSubscriptions.forEach(s => s.unsubscribe());
      this.cameraSubscriptions = [];
      this.allDrivers.forEach(d => {
        const sub = this.cameraStatusService.watchStatus(d.partnerId).subscribe((status: CameraStatus) => {
          this.updateDriverCameraStatus(d.partnerId, status);
        });
        this.cameraSubscriptions.push(sub);
      });
      let currentDriver = this.allDrivers.find(d => d.partnerId == this.config.data.oldDriverData?.driverId);
      if (currentDriver) {
        this.calcDriverPay(currentDriver, this.oldDriverData.payoutAdjustment, true);
      }
      if (this.config.data.selectedDriverId) {
        this.selectedSubDriver = this.allDrivers.find(d => d.partnerId == this.config.data.selectedDriverId) || null;

        this.calcDriverPay(this.selectedSubDriver, this.subDriverForm().value.payoutAdjustment);
      }
    });
  }

  private updateDriverCameraStatus(partnerId: number, status: CameraStatus) {
    const driverIdx = this.allDrivers.findIndex(d => d.partnerId === partnerId);
    if (driverIdx !== -1) {
      const oldDriver = this.allDrivers[driverIdx];
      const updatedDriver = { ...oldDriver, cameraStatus: status };
      this.allDrivers[driverIdx] = updatedDriver;

      // Update filtered drivers incrementally - O(1) operation
      this.updateFilteredDriversIncrementally(oldDriver, updatedDriver);
    }
  }

  private updateFilteredDriversIncrementally(oldDriver: DriverData, updatedDriver: DriverData) {
    const updatedFiltered = CameraFilterUtils.updateFilteredDriversIncrementally(this.filteredDrivers(), oldDriver, updatedDriver, this.currentFilterCode, this.allDrivers);
    this.filteredDrivers.set(updatedFiltered);
  }

  /**
   * Calculate driver payment based on selected driver and adjustments
   */
  calcDriverPay(selectedSubDriver: DriverData | null, payoutAdjustment: number = 0, calcOldDriverPayout = false) {
    if (!selectedSubDriver) return;

    this.checkRequirements(selectedSubDriver);

    const calculations = this.calculateDriverPayments(selectedSubDriver, payoutAdjustment);

    this.updatePaymentValues(calculations, calcOldDriverPayout);
  }

  /**
   * Perform the actual payment calculations
   */
  private calculateDriverPayments(driver: DriverData, payoutAdjustment: number) {
    const blueprintPayout = this.config.data.blueprintDriverPayout;
    const calculatedPayoutTotal = blueprintPayout.CalculatedPayoutTotal;
    const offeringPayoutAfterAdjustment = calculatedPayoutTotal;

    // Handle 100% withhold percentage case (special calculation)
    if (driver.driverDefaultWithholdPercentage === 100) {
      return this.calculateFullWithholdPayments(driver, offeringPayoutAfterAdjustment, payoutAdjustment);
    }

    // Handle normal withhold percentage
    return this.calculatePartialWithholdPayments(driver, offeringPayoutAfterAdjustment, payoutAdjustment);
  }

  /**
   * Calculate payments when driver has 100% withhold percentage
   */
  private calculateFullWithholdPayments(driver: DriverData, offeringPayout: number, payoutAdjustment: number) {
    const percentage = this.getOrganizationPercentage(driver);

    const driverAmount = offeringPayout * (percentage / 100) + payoutAdjustment;
    const providerAmount = offeringPayout + payoutAdjustment;

    return { driverTotalAmount: driverAmount, providerTotalAmount: providerAmount };
  }

  /**
   * Calculate payments with partial withhold percentage
   */
  private calculatePartialWithholdPayments(driver: DriverData, offeringPayout: number, payoutAdjustment: number) {
    const withholdAmount = (driver.driverDefaultWithholdPercentage / 100) * offeringPayout;

    const driverAmount = offeringPayout - withholdAmount + payoutAdjustment;
    const providerAmount = offeringPayout + payoutAdjustment;

    return { driverTotalAmount: driverAmount, providerTotalAmount: providerAmount };
  }

  /**
   * Get the appropriate percentage based on organization type
   */
  private getOrganizationPercentage(driver: DriverData): number {
    const blueprintPayout = this.config.data.blueprintDriverPayout;
    const orgId = driver.organizationId.toString();

    if (driver.isInternalOrganization) {
      return this.getPercentageFromMap(blueprintPayout.SuggestedADROITPayoutPercentage, orgId, blueprintPayout.PriceListSuggestedADROITPercentage);
    } else {
      return this.getPercentageFromMap(blueprintPayout.SuggestedServiceProviderPayoutPercentage, orgId, blueprintPayout.PriceListSuggestedServiceProviderPercentage);
    }
  }

  /**
   * Extract percentage from a map, with fallback to default value
   */
  private getPercentageFromMap(percentageMap: Record<string, number>, orgId: string, fallbackValue: number): number {
    // Check if organization has a specific value
    if (percentageMap[orgId] !== undefined) {
      return percentageMap[orgId];
    }

    // Return fallback if no specific value found
    return fallbackValue;
  }

  /**
   * Update the relevant payment properties based on calculation mode
   */
  private updatePaymentValues(calculations: { driverTotalAmount: number; providerTotalAmount: number }, isOldDriver: boolean) {
    const driverAmount = Number.parseFloat(calculations.driverTotalAmount.toString());
    const providerAmount = Number.parseFloat(calculations.providerTotalAmount.toString());

    if (isOldDriver) {
      this.oldDriverData.driverPayout = driverAmount;
      this.oldDriverData.driverProviderPayout = providerAmount;
    } else {
      this.driverTotalPay = driverAmount;
      this.providerTotalPay = providerAmount;
    }
  }

  checkRequirements(selectedSubDriver: DriverData) {
    if (this.config.data.ridePassengerEquipments?.every((e: number) => JSON.parse(selectedSubDriver.equipmentIds)?.includes(e)) && this.config.data.blueprintVehicleType.toLowerCase() == selectedSubDriver.vehicleType?.toLowerCase()) {
      this.driverMeetsRequirements = true;
    } else {
      this.driverMeetsRequirements = false;
    }
  }

  validateAction() {
    if (this.isSameDriver()) {
      this.showSameDriverAlert();
      return;
    }

    this.openConfirmDialog();
  }

  showSameDriverAlert() {
    this.showAlert = true;
  }

  isSameDriver(): boolean {
    if (this.selectedSubDriver?.partnerId == this.oldDriverData?.driverId) return true;

    return false;
  }

  openConfirmDialog() {
    this.confimActionVisible = true;
  }

  confirmAction() {
    const subDriverForm = this.subDriverForm();
    if (subDriverForm.invalid) return;

    this.loading = true;
    const body = {
      partnerId: this.selectedSubDriver?.partnerId,
      internalNote: this.reason,
      payoutAdjustment: subDriverForm.value.payoutAdjustment == '' ? 0 : subDriverForm.value.payoutAdjustment,
      notifyGuardians: this.notifyChecked,
      notifyOldDriver: this.notifyChecked,
    };

    this.requestBuilder.patch(`ride-drivers/${this.config.data.uuid}/driver`, body).subscribe({
      next: () => {
        this.loading = false;
        this.ref.close({ confirmed: true });
      },
      error: error => {
        this.loading = false;
      },
    });
  }

  filterSuggestions(event: any, autoComplete: AutoComplete) {
    if (this.subDriverForm().form.value.reason) {
      this.filteredSuggestions = this.suggestions.filter(suggestion => suggestion.toLowerCase().includes(this.subDriverForm().form.value.reason.toLowerCase()));
    } else {
      this.filteredSuggestions = [...this.suggestions];
      if (this.filteredSuggestions.length > 0) {
        autoComplete.show();
      }
    }
  }

  customFilterFunction(event: KeyboardEvent, options: SelectFilterOptions): void {
    if (options.filter) {
      options.filter(event);
    } else {
      console.error('Filter function is undefined.');
    }
  }

  ngOnDestroy() {
    this.cameraSubscriptions.forEach(s => s.unsubscribe());
  }
}
